import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createQcenterApiClient, ensureQcenterCsrfCookie } from '../lib/qcenterApiClient';

const DURATION_OPTIONS = [
  { minutes: 5, label: '5 minuti' },
  { minutes: 15, label: '15 minuti' },
  { minutes: 30, label: '30 minuti' },
  { minutes: 60, label: '1 ora' },
  { minutes: 120, label: '2 ore' },
  { minutes: 240, label: '4 ore' },
  { minutes: 480, label: '8 ore' },
];

const DEFAULT_MINUTES = 120;

async function copyTextToClipboard(text) {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

/**
 * Icona condivisione streaming: popup durata, crea il link ospite e lo copia.
 */
export default function CopyStreamingLinkButton({ serial, htkBackend }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(DEFAULT_MINUTES);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const resetTimeoutRef = useRef(null);

  const apiClient = useMemo(
    () => createQcenterApiClient(htkBackend),
    [htkBackend]
  );

  useEffect(() => {
    ensureQcenterCsrfCookie(apiClient);
  }, [apiClient]);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  if (!serial || !htkBackend) {
    return null;
  }

  const closeModal = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (submitting) {
      return;
    }
    setOpen(false);
    setError('');
  };

  const handleOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setError('');
    setDurationMinutes(DEFAULT_MINUTES);
    setOpen(true);
  };

  const handleConfirm = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await apiClient.post('/datalive/streaming-share', {
        serial,
        duration_minutes: Number(durationMinutes),
      });
      const url = response && response.data && response.data.url;
      if (!url) {
        throw new Error('missing url');
      }
      await copyTextToClipboard(url);
      setCopied(true);
      setOpen(false);
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      resetTimeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 403) {
        setError('Non hai il permesso di creare un link di condivisione.');
      } else if (status === 422) {
        setError('Durata o seriale non validi.');
      } else if (status === 401) {
        setError('Sessione scaduta. Accedi di nuovo.');
      } else {
        setError('Creazione del link non riuscita. Riprova.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`infobox-copy-stream-link${copied ? ' is-copied' : ''}`}
        onClick={handleOpen}
        title={copied ? 'Link copiato' : 'Crea e copia link streaming'}
        aria-label={copied ? 'Link streaming copiato' : 'Crea e copia link streaming'}
      >
        <i className={`fas ${copied ? 'fa-check' : 'fa-link'}`} aria-hidden="true" />
      </button>
      {open && createPortal(
        <div
          className="share-stream-modal-overlay"
          onClick={closeModal}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div
            className="share-stream-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-stream-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="share-stream-modal-title">Link streaming ospite</h3>
            <p>Scegli per quanto tempo il link resterà valido. Alla conferma viene copiato negli appunti.</p>
            <label className="share-stream-modal__field" htmlFor="share-stream-duration">
              Durata
              <select
                id="share-stream-duration"
                value={durationMinutes}
                onChange={(event) => setDurationMinutes(Number(event.target.value))}
                disabled={submitting}
              >
                {DURATION_OPTIONS.map((option) => (
                  <option key={option.minutes} value={option.minutes}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {error ? <p className="share-stream-modal__error">{error}</p> : null}
            <div className="share-stream-modal__actions">
              <button type="button" className="share-stream-modal__btn" onClick={closeModal} disabled={submitting}>
                Annulla
              </button>
              <button
                type="button"
                className="share-stream-modal__btn share-stream-modal__btn--primary"
                onClick={handleConfirm}
                disabled={submitting}
              >
                {submitting ? 'Creazione…' : 'Crea e copia link'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
